import { requireDashboardOrigin } from "@playbrain/auth/server/dashboardOriginGuard"
import { requireDashboardAuth } from "@playbrain/auth/server/requireDashboardAuth"
import { dashboardAppearanceCookie } from "@playbrain/config/dashboardAppearance"
import { dashboardApiErrorMessage } from "@server/dashboard-api-errors"
import { serverRouteConfig } from "@server/server-route-config"
import { createFileRoute } from "@tanstack/react-router"
import * as z from "zod"

const updateDashboardProfileAppearanceInputSchema = z.object({
  dashboardAppearance: z.enum(["system", "light", "dark"]),
})

export const Route = createFileRoute("/api/dashboard/profile/appearance")(
  serverRouteConfig({
    server: {
      handlers: {
        POST: async ({ request }) => {
          const originError = requireDashboardOrigin(request)
          if (originError) return originError

          try {
            const input = updateDashboardProfileAppearanceInputSchema.parse(await request.json())
            const auth = await requireDashboardAuth(request, [])
            if ("response" in auth) return auth.response

            const { updateCurrentDashboardProfileAppearance } = await import(
              "@api/features/dashboard-profile/dashboardProfile"
            )
            const profile = await updateCurrentDashboardProfileAppearance({
              dashboardAppearance: input.dashboardAppearance,
              user: auth.user,
            })

            /*
             * The column is authoritative, but it is read after the session
             * resolves — and the `<html>` class has to be decided before that,
             * in the same response that renders the document. So the choice is
             * mirrored into a cookie the root route can read synchronously.
             *
             * Not `HttpOnly`: the client sets this cookie too, when a "system"
             * user's device flips while they are looking at the page. It holds
             * a theme name and nothing else.
             */
            return Response.json(profile, {
              headers: {
                "Set-Cookie": dashboardAppearanceCookie(input.dashboardAppearance),
              },
            })
          } catch (error) {
            return Response.json(
              { error: dashboardApiErrorMessage(error, "Could not save appearance.") },
              { status: 400 }
            )
          }
        },
      },
    },
  })
)
